import { useSimpleStore } from '@passionware/simple-store';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BOOKING_ACTIONS } from 'store/v1/bookings/bookings.constants.js';
import { PRODUCTIONS_ACTIONS } from 'store/v1/productions/productions.constants.js';
import { displayAlert } from 'store/v1/ui/ui.actions.js';
import { findBookingWithinDateRange } from 'v1/helpers/byModel/BookingHelper';
import { getEventEnd, getEventStart } from 'v1/helpers/byModel/EventHelper';
import useEvent from 'v1/helpers/hooks/useEvent';
import useGetContactEvents from 'v1/helpers/hooks/useGetContactEvents';
import { ResourcePane } from 'v5/features/resources/pane/ResourcePane';
import { ColorDot } from 'v5/patterns/widgets/ColorDot';
import { useResourcePickerSource } from 'v5/features/resources/useResourcePickerSource';
import {
  SlotSearchDialog,
  type SlotAssignController
} from 'v5/features/productions/resourcing/slot-search/SlotSearchDialog';
import { useSimpleStoreFromValue } from 'v5/platform/react/useSimpleStoreFromValue';
import { myServices } from 'v5/services/services.connected.app';
import { resourceSearchOfV1 } from 'v5/core/resource-search/v1ResourceSearch';
import { useSlotAssignActions } from './useSlotAssignActions';

interface SlotBooking {
  id: number;
  contact_id?: number;
  status_id?: number;
  resource_slot_assignments?: unknown[];
}

interface SlotSearchDialogConnectedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The production as the v1 store holds it: its id, dates and workspace. */
  production: {
    id: number;
    production_date?: object;
    enterprise_workspace_id?: number | null;
  };
  /** The slot as the production holds it, with its saved search in `query`. */
  slot: { id: number; query?: unknown };
  slotName: string;
}

/**
 * The sidebar slot's "Search all resources": the slot's saved search in the production's
 * workspace, and the slot's ways to assign the one picked (the v1 store's bookings and editor).
 */
export function SlotSearchDialogConnected({
  open,
  onOpenChange,
  production,
  slot,
  slotName
}: SlotSearchDialogConnectedProps) {
  const target = useSimpleStoreFromValue({ production, slot });
  const [controller] = useState<SlotAssignController>(() => ({
    useActionsFor: resources => {
      const { production, slot } = useSimpleStore(target);
      const dispatch = useDispatch();
      const actions = useSlotAssignActions({ production, slot });
      const ids = resources.map(resource => resource.id);
      useGetContactEvents({
        contactIds: ids,
        start_date: getEventStart(production.production_date),
        end_date: getEventEnd(production.production_date),
        // @ts-expect-error the JS hook's `load = []` default types its deps as never[]
        load: [ids.join(',')]
      });
      const bookings = useSelector(
        (state: { bookings?: { data?: Record<string, SlotBooking> } }) =>
          state.bookings?.data
      );
      // as the old search did: "Success" for every booking written while it is open
      useEvent(
        [
          PRODUCTIONS_ACTIONS.CREATE_RESOURCE_SLOT_ASSIGNMENT,
          BOOKING_ACTIONS.CREATE_BOOKING,
          BOOKING_ACTIONS.UPDATE_BOOKING
        ],
        { onSuccess: () => dispatch(displayAlert('success', 'Success')) }
      );
      const bookingOf = (resource: { id: number }): SlotBooking | undefined =>
        findBookingWithinDateRange(
          production.production_date,
          Object.values(bookings ?? {}).filter(
            candidate => candidate.contact_id === resource.id
          )
        )?.[0];
      // one save after another, as the v1 store takes them; the first failure stops the rest.
      // The store's middleware resolves each with its SUCCESS or FAILURE action.
      const saveEach = async (
        save: (
          resource: (typeof resources)[number],
          booking: SlotBooking | undefined
        ) => unknown
      ) => {
        for (const resource of resources) {
          const result = await save(resource, bookingOf(resource));
          const type =
            typeof result === 'object' && result !== null && 'type' in result
              ? String(result.type)
              : '';
          if (!type.endsWith('_SUCCESS')) throw new Error(type);
        }
      };
      const [first] = resources;
      const statuses: { id: number; name?: string; colour?: string }[] =
        actions.statuses;
      return {
        assign: () => {
          if (resources.length === 1) {
            actions.assign(first, bookingOf(first));
            return;
          }
          return saveEach(actions.assignAsIs);
        },
        assignAndEdit: () => {
          actions.assignAndEdit(first, bookingOf(first));
        },
        assignAndConfirm: () => saveEach(actions.assignAndConfirm),
        statuses: statuses.map(status => ({
          key: String(status.id),
          label: status.name,
          icon: status.colour ? <ColorDot color={status.colour} /> : null,
          assign: () =>
            saveEach((resource, booking) =>
              actions.assignWithStatus(status, resource, booking)
            )
        }))
      };
    }
  }));
  const appScope = myServices.appScopeService.useSlugAppScope();
  const source = useResourcePickerSource(myServices);
  return (
    <SlotSearchDialog
      open={open}
      onOpenChange={onOpenChange}
      services={myServices}
      productionId={production.id}
      slotName={slotName}
      // read as the dialog opens: the slot's search as it is then
      preset={resourceSearchOfV1(slot.query)}
      startsAvailable={Boolean(
        getEventStart(production.production_date) &&
          getEventEnd(production.production_date)
      )}
      source={source}
      details={(resource, tab) => (
        <ResourcePane
          resource={resource}
          appScope={appScope}
          isReadOnly
          hideOptions
          initialTab={tab}
        />
      )}
      controller={controller}
    />
  );
}
